/**
 * 卡片信封 —— 契约 §6.1。
 *
 * SSE 的 `data` 事件载荷就是一张卡片：单行 JSON，必带 `type`，命名遵循「域_用途」。
 */
import { z } from 'zod'

/**
 * ⚠⚠ 这里**必须** looseObject(放行未知字段)，**不能** strictObject。
 *
 * strict 会让后端新增的卡片字段被 Zod 挡下来 —— 那等于亲手毁掉
 * 「**新增卡片不需要前端发版**」这条双向承诺(契约 §6.1 第 2 条)。
 * 我们拿到这条承诺的对价，就是「遇到不认识的 type 必须能降级，不能白屏」。
 */
export const cardEnvelopeSchema = z.looseObject({
  type: z.string().min(1),
})
export type CardEnvelope = z.infer<typeof cardEnvelopeSchema>

/**
 * 目前**真的在发**的卡片只有这一张(契约 §6.2)。
 *
 * ⚠ 定名 `auth_login_required`，不是 `login_required` —— 后者只是旧文档没跟上代码，
 *   代码从头到尾发的都是前者(登录是跨域的，故用 `auth_` 前缀)。前端只需认一个。
 */
export const AUTH_LOGIN_REQUIRED = 'auth_login_required'

export const authLoginRequiredCardSchema = z.looseObject({
  type: z.literal(AUTH_LOGIN_REQUIRED),
  message: z.string().nullable().default(null),
})
export type AuthLoginRequiredCard = z.infer<typeof authLoginRequiredCardSchema>

/**
 * `flight_traveler_required` —— 名下没有出行人（机票域**等待点 ⑤**）。
 *
 * ⚠ 与 `auth_login_required` 同一族：都是「这一步缺个前提，去补，补完接着走」的闸门卡。
 *   形状照产出方抄的 —— `flight/card.py` 的 `build_traveler_required_card()`
 *   现在**只放 `need` 一样**，它的注释写明**此刻花名册是空的，没有任何个人信息可放**
 *   （卡片会经 SSE 出网并落一条 `card` 记忆行）。
 *
 * ⚠⚠ 后端在 `_await_roster` 的 interrupt 载荷上挂它，由 `build.py::_interrupt_card`
 *   统一发 —— 与 `flight_options` 同一条路（落库之后才发、不被 resume 重放）。
 *   也就是说这张卡**一直在发**，前端只是没注册，于是一直走 `UnknownCard` 降级成文本。
 *
 * ⚠⚠ **`_MAX_ROSTER_ASKS = 2`** —— 后端只催两次，第二次仍是空名单就收尾、整轮作废。
 *   所以卡片上那个「加好了」入口**不能无条件给**：空点一次就烧掉一次机会。
 *   见 `flight-traveler-required.tsx`。
 */
export const FLIGHT_TRAVELER_REQUIRED = 'flight_traveler_required'

export const flightTravelerRequiredCardSchema = z.looseObject({
  type: z.literal(FLIGHT_TRAVELER_REQUIRED),
  /**
   * 本次要选几位乘机人（后端 `_pax_needed`：成人 + 儿童 + 婴儿）。
   * ⚠ 可缺 —— 缺了就**不说人数**，别默认成 1：说错人数比不说更糟。
   */
  need: z.number().optional(),
  /**
   * 后端组好的催补话术。
   *
   * ⚠⚠ **2026-09-04 起新卡片不再带它** —— 后端把它去掉了，因为同一句话
   *   `build.py` 已经先逐字符 publish 过一遍，卡片再带一份就是让用户连读两遍
   *   （实测直播与回放都重复）。定下的纪律是「卡片是能点的那一层，话术留在气泡里」。
   * ⚠ 字段**保留**、渲染**保留**：那天之前落库的历史行里它还在，
   *   回放要按 `message ?? 兜底文案` 渲染，把它删掉会让老会话的那一屏变空。
   */
  message: z.string().nullable().default(null),
})
export type FlightTravelerRequiredCard = z.infer<typeof flightTravelerRequiredCardSchema>

/**
 * 结果卡片 —— 契约 §6.3（W2）。
 *
 * ⚠⚠ **字段一个都不是猜的**，全部照着产出方的代码抄下来（2026-09-02 核实）：
 *   - `flight_options` 的一项 = `ai_core/domains/flight/agent.py` 的 `_summarize()`
 *     往 `top_options` 里放的那个 dict；
 *   - `hotel_options` 的一项 = `ai_core/domains/hotel/shape.py` 的 `search_view()`。
 *   这正是本文件原来那句「不猜 payload 形状」的落实方式：**去读产出方，而不是自己编一份**。
 *
 * ✅ **两张卡后端已在发**（2026-09-03 落地，见 D 表回签稿）：
 *   `hotel_options` 由 `hotel/present.py` 在**搜索形态**推（键 `items`）；
 *   `flight_options` 由 `flight/card.py` 的 `build_options_card()` 推 —— 它把
 *   `_summarize()` 的产出**原样**套一个 `type`，所以候选键就是 `top_options`
 *   （不是 `options`），卡片里还会带 `schema` / `option_count` / `time_filter` 等字段
 *   （`looseObject` 放行，用不到的不必声明）。
 */

export const FLIGHT_OPTIONS = 'flight_options'
export const HOTEL_OPTIONS = 'hotel_options'

/**
 * 一个航班候选。
 *
 * ⚠ 只有 `flight_no` 是**必需**的：它是「点选走对话继续」的唯一依据
 *   （后端 `_match_choice` 判定顺序里航班号排第一）。没有它这一项就是不可交互的死行，
 *   宁可整张卡降级成文本。其余一律可缺 —— 缺一个字段就白屏，是拿用户的界面赌后端不改字段。
 */
export const flightOptionSchema = z.looseObject({
  /** M2 选择("就第一个")靠它对应报价。 */
  quote_id: z.string().optional(),
  /** 形如 `2026-09-03T07:00:00`。⚠ 不带时区，展示时按 Asia/Shanghai 理解（产出方写死的）。 */
  depart: z.string().optional(),
  arrive: z.string().optional(),
  /** 机场三字码。 */
  from: z.string().optional(),
  to: z.string().optional(),
  airline: z.string().optional(),
  flight_no: z.string().min(1),
  /** 经停数。0 = 直飞。 */
  stops: z.number().optional(),
  cabin: z.string().optional(),
  /** ⚠ 元，**可能带小数**（产出方 `_money_yuan` 回的是 `float | None`）。null = 暂无报价。 */
  total_price_yuan: z.number().nullable().optional(),
  /** ⚠ 供应商语义：9 = 9 个及以上，0 = **未知**（不是「没座位」）。 */
  seats_remaining: z.number().optional(),
  expire_at_ms: z.number().optional(),
})
export type FlightOption = z.infer<typeof flightOptionSchema>

export const flightOptionsCardSchema = z.looseObject({
  type: z.literal(FLIGHT_OPTIONS),
  title: z.string().nullable().optional(),
  query: z
    .looseObject({
      from: z.string().optional(),
      to: z.string().optional(),
      date: z.string().optional(),
      adults: z.number().optional(),
    })
    .optional(),
  /**
   * 供应商返回的总数（**过滤/重排前**）。`top_options` 是过滤后的前 5（后端 `_TOP_N`）。
   * 两者不等 = 只展示了一部分，卡片头据此显示「共 N 个，展示前 M」。
   */
  option_count: z.number().optional(),
  /**
   * 候选数组。键名就是 `top_options` —— 后端 `build_options_card()` 把 `_summarize()`
   * 的产出**原样**发出，产出方内部就叫这个名（`flight/agent.py`）。
   *
   * ⚠ 2026-09-03 之前这里曾同时收 `options`（占位夹具用的名）。键名拍定 `top_options` 后
   *   已删掉那一支；后端 `flight/card.py:87` 的注释也约好了「不再兼容一份」——
   *   **两边都别再把 `options` 加回来**，否则又变成谁也不敢先删的兼容包袱。
   */
  top_options: z.array(flightOptionSchema).optional(),
})
export type FlightOptionsCard = z.infer<typeof flightOptionsCardSchema>

/** 取候选数组。键名见 {@link flightOptionsCardSchema}。 */
export function flightOptionsOf(card: FlightOptionsCard): FlightOption[] {
  return card.top_options ?? []
}

/**
 * 一家候选酒店。字段照 `hotel/shape.py` 的 `search_view()`。
 *
 * ⚠ 只有 `idx` 是**必需**的：酒店那边「选第几家」靠的就是展示序号
 *   （后端 `hotel/graph.py` 用 `hotel_idx` 把序号翻译成 `hotel_id`，序号优先、名字兜底）。
 *   序号对不上，点选就是错的 —— 那比不能点更糟。
 */
export const hotelOptionSchema = z.looseObject({
  /** 展示序号，从 1 开始。⚠ 必须与后端 `hotel_idx` 同源，别在前端重新编号。 */
  idx: z.number(),
  /** 稳定公开标识，可进日志/记忆/跨会话引用。 */
  hotel_id: z.string().optional(),
  name: z.string().optional(),
  /** 星级**文本**（如「四星级」）。⚠ 不是数字；供应商没给显示名时可能是空串。 */
  star: z.string().optional(),
  address: z.string().optional(),
  /** 评分，一位小数。null = 没有评分。 */
  score: z.number().nullable().optional(),
  /** ⚠ **整数**元（产出方 `money_yuan` 回 `int | None`）。null = **暂未报价**，不是 0。 */
  price_yuan: z.number().nullable().optional(),
})
export type HotelOption = z.infer<typeof hotelOptionSchema>

export const hotelOptionsCardSchema = z.looseObject({
  type: z.literal(HOTEL_OPTIONS),
  title: z.string().nullable().optional(),
  /** ⚠ 键名是 `items` —— `hotel/present.py:271` 那行注释掉的 publish 里写死的。 */
  items: z.array(hotelOptionSchema).optional(),
})
export type HotelOptionsCard = z.infer<typeof hotelOptionsCardSchema>

/**
 * `flight_confirmation` —— 金额确认闸门（机票域**等待点 ③**，2026-09-04 后端放开出网）。
 *
 * ★ **这是整条漏斗里最贵的一步**：点下「确认」就进下单。此前这一步**只有纯文本**，
 *   界面上一个能点的东西都没有，用户得手打「确认」两个字。结构化数据一直是齐的
 *   （`flight/card.py` 的 `build_confirmation_card()`），缺的只是那个出口。
 *
 * ⚠⚠ **这张卡不重排文字。** 同一轮的 assistant 气泡里已经有 `compose_card_text()`
 *   拼出的全文（航班、票价拆分、行李、退改、出票时限逐行都在）。卡片再排一遍
 *   就是让用户把同一段字读两遍 —— 那正是 `flight_traveler_required` 犯过的错
 *   （后端 2026-09-04 已把它的 `message` 去掉）。
 *   **所以组件只做「摘要一行 + 三个按钮」**，产出方 `build_confirmation_sse_card()`
 *   的文档注释里也约好了这一条。字段在这里声明全，是为了写在契约上、供以后取用，
 *   不是让组件全渲染出来。
 *
 * ⚠ 字段照 `build_confirmation_card()` 逐个抄（2026-09-04 核实）。
 */
export const FLIGHT_CONFIRMATION = 'flight_confirmation'

/** 一档旅客的票价拆分。金额单位**元**，可能带小数（产出方 `_money_yuan` 回 `float | None`）。 */
export const fareBreakdownSchema = z.looseObject({
  /** 中文旅客类型：`成人` / `儿童` / `婴儿`。⚠ 不是 `ADULT` 这类码。 */
  pax: z.string().optional(),
  base_yuan: z.number().nullable().optional(),
  tax_yuan: z.number().nullable().optional(),
  fuel_yuan: z.number().nullable().optional(),
  per_ticket_yuan: z.number().nullable().optional(),
})
export type FareBreakdown = z.infer<typeof fareBreakdownSchema>

export const baggageRuleSchema = z.looseObject({
  pax: z.string().optional(),
  /** 已经组好的中文，如「托运1件」/「托运20KG」。⚠ 别在前端重新拼，数值可能整个缺。 */
  text: z.string().optional(),
})

export const ticketRuleSchema = z.looseObject({
  pax: z.string().optional(),
  /** 时间窗，如「起飞前2至24小时」/「任意时间」。 */
  window: z.string().optional(),
  text: z.string().optional(),
  endorsement: z.string().optional(),
})

export const flightConfirmationCardSchema = z.looseObject({
  type: z.literal(FLIGHT_CONFIRMATION),
  /**
   * 航班身份。⚠ 取自**选中的候选**，不是验价响应（后者不含航段明细）——
   * 所以 `depart` / `arrive` 与 `flight_options` 里是同一类值：
   * **不带时区的本地时间串**，按 Asia/Shanghai 理解。
   */
  flight: z
    .looseObject({
      airline: z.string().nullable().optional(),
      flight_no: z.string().nullable().optional(),
      depart: z.string().nullable().optional(),
      arrive: z.string().nullable().optional(),
      from: z.string().nullable().optional(),
      to: z.string().nullable().optional(),
      stops: z.number().nullable().optional(),
    })
    .optional(),
  cabin: z.string().nullable().optional(),
  /** 只放非零的档（产出方 `if query.get(k)`），所以缺键 = 这一档没人。 */
  passengers: z
    .looseObject({
      adults: z.number().optional(),
      children: z.number().optional(),
      infants: z.number().optional(),
    })
    .optional(),
  /** ★ 用户据以点头的那个数，**元**。null = 验价没给出总价，那就别显示金额。 */
  total_price_yuan: z.number().nullable().optional(),
  fare_breakdowns: z.array(fareBreakdownSchema).optional(),
  baggage: z.array(baggageRuleSchema).optional(),
  ticket_rules: z.array(ticketRuleSchema).optional(),
  /** 出票时限，后端原话。 */
  billing_description: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  /**
   * ⚠⚠ 报价过期时刻，**带时区的 ISO**（产出方是 `datetime(tz=UTC).isoformat()`）——
   *   与 `flight` 里那两个**不带**时区的时间串不是一回事，这个可以直接 `new Date()`。
   * ⚠ 过期了**不要禁用「确认」**：后端 `_CONFIRM_EXPIRED_NOTICE` 那条路径会自动重新
   *   验价再问一遍，禁掉反而把唯一的出路堵死。照实说一句「可能需要重新验价」就够。
   */
  expires_at: z.string().nullable().optional(),
  /** 同上，后端按 Asia/Shanghai 格式化好的 `MM-DD HH:mm`，展示直接用它，别自己算。 */
  expires_at_local: z.string().nullable().optional(),
  /** ⚠ 供应商语义同 `flight_options`：9 = 9 个及以上，0 = **未知**（不是没座位）。 */
  seats_remaining: z.number().nullable().optional(),
  quote_id: z.string().nullable().optional(),
})
export type FlightConfirmationCard = z.infer<typeof flightConfirmationCardSchema>
